package fr.frux.shared.dto

import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Participant d'une récolte, avec pseudo (#206) — même sémantique que `TreeDto.createdByUsername` :
 * pseudo réel renseigné par jointure Users, ou null si absent. Jamais un id maquillé en nom.
 */
@Serializable
data class HarvestParticipantDto(
    val id: Long,
    val username: String? = null,
)

@Serializable
data class HarvestDto(
    val id: Long? = null,
    // Nul pour une récolte de lieu (#617). Les récoltes de lieu sont filtrées pour les clients sans
    // X-Frux-Features: le build Play v22 a `treeId` non nullable.
    val treeId: Long? = null,
    // Cible lieu d'une récolte (#617), exclusive de [treeId] ; filtrée pour les clients sans X-Frux-Features.
    val siteId: Long? = null,
    // Nom du lieu, joint côté serveur (#617) pour afficher la carte sans redemander le lieu.
    val siteName: String? = null,
    // Récolte à laquelle le propriétaire du lieu invite (#617) ; défaut false = rétro-compatible.
    val hostedByOwner: Boolean = false,
    val createdBy: Long,
    // Pseudo de l'organisateur, renseigné par jointure Users (#206) ; même sémantique que
    // TreeDto.createdByUsername. Nullable = rétro-compatible côté sérialisation.
    val createdByUsername: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val scheduledDate: Instant,
    val description: String? = null,
    // Historiquement déprécié au profit de [participantsDetails] (#206), mais redevenu la source
    // nominale des destinataires côté serveur (#515, ADR 0029) : [participantsDetails] peut être
    // amputé des pseudos masqués par le viewer, tandis que [participants] reste la liste complète
    // des identifiants par construction (`HarvestDao`). Encore utile côté client pour l'affichage
    // simple sans pseudo.
    val participants: List<Long> = emptyList(),
    val status: HarvestStatus = HarvestStatus.PLANNED,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant? = null,
    /** Capacité de la récolte (#165). `null` = sans limite ; sinon bornée à 2..30. */
    val maxParticipants: Int? = null,
    // Détail des participants avec pseudo (#206), organisateur inclus, en tête de liste. Vide par
    // défaut = rétro-compatible.
    val participantsDetails: List<HarvestParticipantDto> = emptyList(),
    // Nombre de participants, organisateur inclus (#277) : seul champ de participation servi aux
    // appels anonymes, où [participants] et [participantsDetails] sont vidés. Défaut 0 =
    // rétro-compatible côté sérialisation.
    val participantsCount: Int = 0,
    // Expiration dérivée côté serveur (#435) : date prévue passée de plus d'un délai fixe (24 h
    // pour une PLANNED, 12 h pour une ONGOING). Jamais un membre de [HarvestStatus], jamais écrit
    // en base : le join est refusé, les transitions de l'organisateur restent permises. Défaut
    // false = rétro-compatible côté sérialisation.
    val expired: Boolean = false,
)

@Serializable
data class CreateHarvestRequest(
    // Exactement un de [treeId] et [siteId] (#617), validé par le service ; défauts nuls pour que
    // le corps des anciens clients, qui n'envoient que treeId, reste valide.
    val treeId: Long? = null,
    val siteId: Long? = null,
    // Invitation du propriétaire du lieu (#617), refusée par le service si l'auteur n'est pas propriétaire.
    val hostedByOwner: Boolean = false,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val scheduledDate: Instant,
    val description: String? = null,
    val maxParticipants: Int? = null,
)

@Serializable
data class HarvestListResponse(
    val harvests: List<HarvestDto>,
    val count: Int,
)

/**
 * Résumé de l'arbre porté par une récolte de proximité (#166) : le strict nécessaire pour
 * afficher une carte de liste sans redemander l'arbre complet — sur le modèle de `SpeciesRefDto`
 * imbriqué dans `TreeDto` pour la carte.
 */
@Serializable
data class NearbyHarvestTreeSummaryDto(
    val treeId: Long,
    val speciesCommonName: String,
    val treeStatus: TreeStatusDto,
)

/** Résumé du lieu porté par une récolte de lieu de proximité (#617), pendant de [NearbyHarvestTreeSummaryDto]. */
@Serializable
data class NearbyHarvestSiteSummaryDto(
    val siteId: Long,
    val name: String,
    val accessRule: SiteAccessRuleDto,
)

/**
 * Récolte à venir dans un rayon (#166), enrichie de la distance et d'un résumé d'arbre — les deux
 * champs qu'une liste de proximité affiche et qu'une fiche `HarvestDto` seule ne porte pas.
 */
@Serializable
data class NearbyHarvestDto(
    val id: Long,
    // Nul pour une récolte de lieu (#617), filtrée pour les clients sans X-Frux-Features (v22 non nullable).
    val treeId: Long? = null,
    // Cible lieu (#617), exclusive de [treeId] ; filtrée pour les clients sans X-Frux-Features.
    val siteId: Long? = null,
    // Invitation du propriétaire (#617) ; défaut false = rétro-compatible.
    val hostedByOwner: Boolean = false,
    val createdBy: Long,
    // Pseudo de l'organisateur, même sémantique que HarvestDto.createdByUsername (#206).
    val createdByUsername: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val scheduledDate: Instant,
    val description: String? = null,
    // Déprécié au profit de [participantsDetails] (#206) — même remarque que HarvestDto.participants.
    val participants: List<Long> = emptyList(),
    val status: HarvestStatus = HarvestStatus.PLANNED,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant? = null,
    val maxParticipants: Int? = null,
    val distanceMeters: Double,
    // Nul pour une récolte de lieu (#617). Le build v22 le lit non nul, d'où le filtrage
    // des clients sans X-Frux-Features.
    val tree: NearbyHarvestTreeSummaryDto? = null,
    // Résumé du lieu d'une récolte de lieu (#617), exclusif de [tree] ; filtré comme [tree].
    val site: NearbyHarvestSiteSummaryDto? = null,
    // Détail des participants avec pseudo (#206), organisateur en tête. Vide par défaut = rétro-compatible.
    val participantsDetails: List<HarvestParticipantDto> = emptyList(),
    // Même sémantique que HarvestDto.participantsCount (#277).
    val participantsCount: Int = 0,
    // Même sémantique que HarvestDto.expired (#435). La liste de proximité ne sert que des
    // récoltes à venir, le champ y reste false en pratique.
    val expired: Boolean = false,
)

/**
 * Enveloppe de `GET /api/v1/harvests?lat&lon&radius` (#166). `total` porte le compte serveur dans le
 * rayon (leçon #155 — la troncature du lot doit rester visible), nom aligné sur `TreeListResponse.total`.
 */
@Serializable
data class NearbyHarvestsResponse(
    val harvests: List<NearbyHarvestDto>,
    val total: Int,
)

@Serializable
data class UpdateHarvestStatusRequest(
    val status: HarvestStatus,
)

@Serializable
enum class HarvestStatus {
    PLANNED,
    ONGOING,
    COMPLETED,
    CANCELLED,
    ;

    /** États terminaux : plus aucune transition ni join/leave possibles. */
    val isTerminal: Boolean
        get() = this == COMPLETED || this == CANCELLED

    /** Une récolte n'accepte de nouveaux participants que tant qu'elle est à venir ou en cours. */
    val acceptsParticipants: Boolean
        get() = this == PLANNED || this == ONGOING

    /** Transitions autorisées par l'organisateur (machine à états stricte, cf. #31). */
    fun canTransitionTo(target: HarvestStatus): Boolean =
        target in
            when (this) {
                PLANNED -> setOf(ONGOING, CANCELLED)
                ONGOING -> setOf(COMPLETED, CANCELLED)
                COMPLETED, CANCELLED -> emptySet()
            }
}
